/**
 * Whether the sandbox beside a chat is still the one the chat is about (LO-206),
 * without React or a network, so it can be read and tested on its own.
 *
 * ## The problem
 *
 * A sandbox belongs to a saved draft. After the author asks the builder for a
 * change, the chat shows the builder working and the panel went on saying
 * *ready*, with every step *done*, for a draft that is no longer what is being
 * made. A new sandbox only starts when the builder saves a new draft that
 * replaces the old one, so for the minutes in between the panel looked current
 * when it was not, and Submit for approval was live for the old build.
 *
 * ## What it reads, and what it does not
 *
 * Only what the chat's own view (`GET /builder/sessions/{id}`) and the draft's
 * request already carry: the messages in the order they were made (the person's
 * words, the builder's tool calls, its refusals), the session's status, the
 * newest build the agent made (`build_id`) and the draft's own build
 * (`proposed.build_id`) and saved time (`requested_at`). Nothing was added to the
 * view for this.
 *
 * Three signals say the chat has moved past the draft, and any one is enough:
 *
 *  - the person wrote after the draft was saved (a message after the last save);
 *  - the agent made a build after saving, or the build it made last is not the
 *    draft's (it is rebuilding on its own, after a failed run for example);
 *  - this tab has just sent a message the chat's view does not hold yet, so the
 *    panel does not wait a poll to say so.
 *
 * What is **not** a signal: the builder being at work. The agent waits on its own
 * sandbox for ten minutes by polling `sandbox_status` with its session running
 * (LO-184), and that is the draft being tested, not a revision.
 *
 * ## What it does not prove
 *
 * That a message was a request for a change. The panel cannot tell "make it more
 * interactive" from "what does this do?"; it says the chat moved on, and it holds
 * Submit only while the builder is actually working on something, never because
 * somebody wrote. The kernel's gate is untouched and does not know any of this: it
 * binds a pass to the draft's own build and manifest and nothing else. The hold is
 * the Builder's own rule and says so where it is read.
 */
import { awaitingReply } from "./poll.js";
import { STATE_WORD, headline, runOf, when } from "./sandboxPanel.js";

/** A build id in the short form the panel names it by: a build id is a UUID, and
 *  its first eight characters are what a person can read out. */
export const shortBuild = (id) => String(id || "").slice(0, 8);

/** How many messages in the chat are the person's. */
export const countUsers = (messages) => (messages || []).filter((m) => m?.role === "user").length;

/**
 * Where each save of a draft is, in the messages: the `request_manifest` calls
 * the kernel did not refuse. A call leaves one entry when it is made and a result
 * leaves nothing, so a refusal is the only thing that says a call saved nothing,
 * and it is paired with the call before it.
 */
function saveIndices(messages) {
  const saves = [];
  (messages || []).forEach((m, i) => {
    if (m?.role === "tool" && m.name === "request_manifest") saves.push(i);
    else if (m?.role === "tool_error" && m.name === "request_manifest") saves.pop();
  });
  return saves;
}

/** How many drafts the chat has saved (or has a save on its way for). */
export const countSaves = (messages) => saveIndices(messages).length;

/** The place of the last save in the messages, or -1 when there is none. */
export function draftSavedAt(messages) {
  const saves = saveIndices(messages);
  return saves.length ? saves[saves.length - 1] : -1;
}

// -- the steps ------------------------------------------------------------------------------

/** The steps a revision goes through, in order. The last is never this panel's:
 *  the sandbox for a new draft starts when the draft is saved, and is drawn by the
 *  panel for that draft. */
export const REVISION_STEPS = [
  { key: "working", label: "Builder working" },
  { key: "build", label: "Build" },
  { key: "check", label: "Code check" },
  { key: "saved", label: "Draft saved" },
  { key: "sandbox", label: "New sandbox" },
];

/** What a revision step is called. Never the colour alone. `skipped` is a step the
 *  builder did not run for this revision (a change to the manifest alone needs no
 *  build), and it is not called done. */
export const REVISION_STEP_WORD = {
  done: "done",
  active: "in progress",
  waiting: "waiting",
  skipped: "not run",
};

const BUILD_TOOLS = ["submit_app", "build_status"];
const CHECK_TOOLS = ["check_build", "run_qa", "qa_status"];

function stepsOf({ tail, newerSaves, from, building }) {
  const names = new Set(tail.filter((m) => m?.role === "tool").map((m) => m.name));
  const reached = [
    tail.some((m) => m?.role === "tool" || m?.role === "agent"),
    BUILD_TOOLS.some((n) => names.has(n)),
    CHECK_TOOLS.some((n) => names.has(n)),
    newerSaves.some((i) => i >= from),
  ];
  const furthest = reached.lastIndexOf(true);
  const keys = ["working", "build", "check", "saved"];
  const out = keys.map((key, i) => {
    let state = "waiting";
    if (furthest === -1) state = i === 0 ? "active" : "waiting";
    else if (i < furthest) state = i === 0 || reached[i] ? "done" : "skipped";
    else if (i === furthest) state = key === "build" && building === "done" ? "done" : "active";
    return { key, label: REVISION_STEPS[i].label, state };
  });
  out.push({ key: "sandbox", label: REVISION_STEPS[4].label, state: "waiting" });
  return out;
}

// -- the words ------------------------------------------------------------------------------

const BASIS = "This hold is the Builder's own. The kernel's gate does not know a revision or a newer build exists: "
  + "it accepts a draft whose run passed for its own build and manifest.";

/**
 * What Submit says while a revision is in progress, or `null` when nothing holds
 * it. `reason` goes beside the button, and `basis` under it says whose rule this is.
 */
export function submitHold(revision) {
  return revision?.blocksSubmit ? revision.hold : null;
}

function holdOf(cause, draft) {
  const what = draft.buildShort ? `the previous draft (build ${draft.buildShort})` : "the previous draft";
  const reason = cause === "asked"
    ? `A revision is being built, so this panel holds Submit for approval: this sandbox tests ${what}, not the revision. `
      + "It is available again once the builder saves the new draft and a QA run passes for it."
    : `A newer build is being made, so this panel holds Submit for approval: this sandbox tests ${what}, not that build. `
      + "It is available again once the builder saves the new draft and a QA run passes for it.";
  return { reason, basis: BASIS };
}

function wordsOf({ cause, working, ended, draft, newBuild, askedAt }) {
  const which = draft.savedAt ? `the draft saved at ${when(draft.savedAt)}` : "the saved draft";
  const build = draft.buildShort ? ` (build ${draft.buildShort})` : "";
  if (working) {
    const second = cause === "asked"
      ? `A revision was asked for ${askedAt ? `at ${new Date(askedAt).toLocaleString()}` : "after this draft was saved"} and is being built.`
      : `The builder is making a newer version${newBuild ? ` (build ${newBuild.short})` : ""} than the one this sandbox tests.`;
    return {
      title: "This sandbox is for the previous draft",
      lines: [
        `It tests ${which}${build}.`,
        second,
        "A new sandbox starts when the builder saves the new draft, and this panel switches to it. "
          + "Until then nothing below is a test of the revision.",
      ],
    };
  }
  if (cause === "asked") {
    return {
      title: "A message came after this draft was saved",
      lines: [
        `This sandbox is for ${which}${build}.`,
        ended
          ? "You wrote to the builder after it, and the chat has ended without a new draft, so this sandbox is still for the latest saved draft."
          : "You wrote to the builder after it. It replied and has not saved a new draft, so this sandbox is still for the latest saved draft.",
      ],
    };
  }
  return {
    title: "A newer build was made after this draft was saved",
    lines: [
      `This sandbox is for ${which}${build}.`,
      `A newer build${newBuild ? ` (build ${newBuild.short})` : ""} was made after it. It is not saved as a draft, `
        + "and the builder is not working on it now, so this sandbox is still for the latest saved draft.",
    ],
  };
}

// -- the verdict ----------------------------------------------------------------------------

/**
 * Whether the chat has moved past this draft, and what to say and hold about it,
 * or `null` when it has not (or there is nothing to compare).
 *
 *  - `chat` is the chat's view: `messages`, `status`, `build_id`.
 *  - `request` is the draft the panel is on, as the kernel's view sends it.
 *  - `asked` is a message this tab has just sent, `{ at, users }` (the time in
 *    milliseconds and how many of the person's messages the view held when it was
 *    sent), so the panel does not wait for a poll to hold Submit. Once the view
 *    holds the message the view decides and `asked.at` is only the time to show.
 *  - `savesAtMount` is how many saves the chat held when the panel for this draft
 *    first saw it. A save beyond that is a newer draft on its way: nothing in the
 *    view says which request a call produced, but a panel for a draft cannot have
 *    mounted before the call that made it, so a later one is not this draft's.
 *    Without it every save is taken as this draft's own, which is right for a panel
 *    mounted after a reload and misses only the length of one call.
 *
 * `blocksSubmit` is the only thing that holds the button, and only while the
 * builder owes a reply or is at work: a chat that moved on and went quiet is said,
 * and holds nothing.
 */
export function revisionOf({ chat, request, asked = null, savesAtMount = null }) {
  if (!chat || !request || request.state !== "draft") return null;
  const messages = chat.messages || [];
  if (!messages.length) return null;

  const saves = saveIndices(messages);
  const own = savesAtMount === null || savesAtMount === undefined ? saves.length : Math.min(savesAtMount, saves.length);
  const saved = own ? saves[own - 1] : -1;
  const newerSaves = saves.slice(own);

  const userIdx = messages.findIndex((m, i) => i > saved && m?.role === "user");
  const pending = Boolean(asked) && countUsers(messages) <= (asked.users ?? 0);
  const buildIdx = messages.findIndex((m, i) => i > saved && m?.role === "tool" && m.name === "submit_app");
  const draftBuild = String(request.proposed?.build_id || "");
  const newId = chat.build_id && draftBuild && chat.build_id !== draftBuild ? String(chat.build_id) : "";

  if (userIdx < 0 && !pending && buildIdx < 0 && !newId && !newerSaves.length) return null;

  const cause = userIdx >= 0 || pending ? "asked" : "rebuilding";
  const working = awaitingReply(chat) || pending;
  const draft = { buildId: draftBuild, buildShort: shortBuild(draftBuild), savedAt: String(request.requested_at || "") };
  const newBuild = newId ? { id: newId, short: shortBuild(newId) } : null;
  const askedAt = asked?.at ?? null;

  const starts = [userIdx, buildIdx, newerSaves[0] ?? -1].filter((i) => i >= 0);
  const from = starts.length ? Math.min(...starts) : pending ? messages.length : saved + 1;
  const steps = stepsOf({
    tail: messages.slice(from), newerSaves, from,
    building: newBuild && chat.build_ok ? "done" : "",
  });

  const words = wordsOf({ cause, working, ended: chat.status === "terminated", draft, newBuild, askedAt });
  return {
    cause,
    working,
    blocksSubmit: working,
    severity: "info",
    title: words.title,
    lines: words.lines,
    chip: working ? "out of date" : "",
    draft,
    newBuild,
    askedAt,
    steps,
    hold: working ? holdOf(cause, draft) : null,
  };
}

// -- the old run, kept and dated --------------------------------------------------------------

/**
 * The previous build's result in one sentence, for the line a collapsed "Previous
 * build" carries. It dates the draft and the run, and says a pass only as the
 * kernel does (`headline`), followed by whose it is: the previous build's, which
 * is not a result for the revision.
 */
export function previousBuildLine({ draft, box, qa }) {
  const named = draft?.buildShort ? `Build ${draft.buildShort}` : "The saved draft";
  const saved = draft?.savedAt ? `, saved ${when(draft.savedAt)}` : "";
  const sandbox = box ? `Sandbox ${STATE_WORD[box.state] || box.state}` : "No sandbox yet";
  const run = runOf(qa);
  let ran = "no QA run";
  if (run) {
    const view = headline(qa);
    const id = run.run_id || "run";
    const done = run.finished_at ? `, finished ${when(run.finished_at)}` : "";
    ran = {
      running: `QA run ${id} is still running`,
      passed: `QA run ${id} ${view.text}, for the previous build${done}`,
      stale: `QA run ${id} passed for earlier permissions${done}`,
      failed: `QA run ${id} failed${done}`,
      inconclusive: `QA run ${id} was inconclusive${done}`,
      error: `QA run ${id} did not complete${done}`,
    }[view.tone] || `QA run ${id}: ${String(view.word || "").toLowerCase()}${done}`;
  }
  return `${named}${saved}. ${sandbox}; ${ran}. This is the previous build's result, not a result for the revision.`;
}

/**
 * What the panel for a new draft says about the one it replaced. The replacement
 * and the removal of the old sandbox are the kernel's and the control plane's
 * (a replaced draft's sandbox goes with it), and this only reports what they do.
 */
export function replacedLine(previous) {
  const build = previous?.buildId ? shortBuild(previous.buildId) : "";
  const saved = previous?.savedAt ? `saved at ${when(previous.savedAt)}` : "";
  const named = [build ? `build ${build}` : "", saved].filter(Boolean).join(", ");
  return "This is the sandbox for the draft the builder saved after your revision. "
    + `The previous draft${named ? ` (${named})` : ""} is replaced, and its sandbox is removed with it.`;
}

// -- the trail of drafts ------------------------------------------------------------------------

const TRAIL = 4;

/** The drafts this tab has shown for a chat, oldest first, each once. A fact is
 *  `{ id, buildId, savedAt }`; one with no id is not a draft and is not kept. */
export function noteDraft(trail, fact) {
  if (!fact?.id || trail.some((d) => d.id === fact.id)) return trail;
  return [...trail, fact].slice(-TRAIL);
}

/** The draft shown just before `id`, or `null`: the first this tab saw replaced
 *  nothing it saw. */
export function previousOf(trail, id) {
  const at = trail.findIndex((d) => d.id === id);
  return at > 0 ? trail[at - 1] : null;
}
